import PostAvatar from "@/components/ui/PostAvatar";
import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import "./style.css";
import { enqueueSnackbar } from "notistack";
import { getUserById, getUserDataByUserId, getUserDetailsForTooltip, getUserLastIpByUserId } from "@/api/user";
import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import Modal from "@/components/ui/modal";
import ChatConfirmationModal from "@/components/messages-comps/chat-room-by-id/chat-confirmation-modal";
import SolidButton from "@/components/ui/Buttons/SolidButton";
import { createFollowerData, deleteFollowerData } from "@/api/followers";
import { useTranslations } from "@/lib/static-intl";
import { getIdentifier } from "@/utils/helpers";
import { CLOUDFRONT_BASE_URL } from "../../../../../../config";
import { EVENT_TYPES, sendAnalyticsEvent } from "@/utils/AnalyticsEvents";
import { buildContentPermanentPath } from "@/constants/routes";

const TooltipUserNameheading = ({
  children,
  username = null,
  currentUserFollowing,
  setCurrentUserFollowing,
}) => {
  const apiT = useTranslations("api");
  const explorePostDetailsT = useTranslations("explore.explore_post_details");
  const lastLocation = useTranslations("user_profile");
  const { user, openLoginModal } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [currentUserIpLocation, setCurrentUserIpLocation] = useState(null);
  const [isUserLocationOn, setIsUserLocationOn] = useState(false);
  const userPosts = userDetails?.posts || userDetails?.post || [];

  const fetchUserProfileById = async (userId) => {
    try {
      setLoading(true);
      const response = await getUserById(userId);
      if (response?.id) {
        setIsUserLocationOn(response.location);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  }

  const followThisUser = async (userIdToBeFollowed) => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!userIdToBeFollowed) return;

    try {
      setLoader(true);
      const payload = {
        followingId: userIdToBeFollowed,
        followerId: user?.userId,
      };
      const response = await createFollowerData(payload);
      sendAnalyticsEvent(EVENT_TYPES.newFans, userIdToBeFollowed);
      setCurrentUserFollowing?.([
        ...currentUserFollowing,
        {
          id: response?.id,
          userId: userIdToBeFollowed,
        },
      ]);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    } finally {
      setLoader(false);
    }
  };

  const handleUnfollowUser = async () => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      const id = currentUserFollowing.find(
        (_f) => _f?.userId === userDetails?.id
      )?.id;

      if (!id) return;

      setLoader(true);
      await deleteFollowerData(id);

      setCurrentUserFollowing(
        currentUserFollowing?.filter((_f) => _f?.id !== id)
      );

      setIsOpen(false);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || error?.message || apiT("api error"),
        {
          variant: "error",
        }
      );
    } finally {
      setLoader(false);
    }
  };

  function isFollowing(currentUserFollowing, selectedUser) {
    if (!selectedUser) return false;

    let result = false;

    currentUserFollowing?.forEach((_f) => {
      if (_f?.userId == selectedUser?.id) {
        result = true;
      }
    });

    return result;
  }

  const fetchUserDeatilsForTooltip = async (userName) => {
    if (userName) {
      try {
        setLoading(true);
        const response = await getUserDetailsForTooltip({ username: userName });
        if (response?.id) {
          setUserDetails(response);
        }
      } catch (error) {
        enqueueSnackbar(
          error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
          {
            variant: "error",
          }
        );
      } finally {
      }
    }
  };

  const fetchUserLastIp = async (userId) => {
    try {
      setLoading(true);
      const response = await getUserLastIpByUserId(userId);
      if (Array.isArray(response) && response[0]?.ipLocation) {
        const reformattedIpLocations =
          `${response[0]?.ipLocation?.split(",")?.reverse()[1]?.trim()}` +
          `, ${response[0]?.ipLocation?.split(",")?.reverse()[0]?.trim()}`;
        if (reformattedIpLocations)
          setCurrentUserIpLocation(reformattedIpLocations);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        { variant: "error" }
      );
    } finally {
    }
  };

  useEffect(() => {
    if (userDetails) {
      fetchUserLastIp(userDetails?.id);
      fetchUserProfileById(userDetails?.id);
    }
  }, [userDetails]);

  useEffect(() => {
    if (isVisible) {
      const unformattedUserName = username;
      if (unformattedUserName?.startsWith("@")) {
        fetchUserDeatilsForTooltip(unformattedUserName?.slice(1));
      } else {
        fetchUserDeatilsForTooltip(unformattedUserName);
      }
    }
  }, [isVisible, username, user]);

  return (
    <>
      <Tooltip
        title={
          <div
            className={`dark:bg-brown border dark:border-brown w-[360px] min-h-[220px] max-h-[240px] bg-white rounded-2xl py-5 px-5 flex flex-col gap-2 items-center justify-start overflow-hidden`}
          >
            {loading ? (
              <div className="w-fit h-[200px] flex flex-col items-center justify-center gap-1 text-black/40 dark:text-white/40">
                <Iconify
                  icon={"eos-icons:loading"}
                  className={"!text-inherit"}
                />
                <span className="text-inherit">
                  {explorePostDetailsT("finding_more")}
                </span>
              </div>
            ) : (
              <>
                <div className="w-full flex items-center justify-between gap-5">
                  <div
                    className="w-fit flex items-center gap-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        getIdentifier(userDetails?.username, userDetails?.id),
                        "_blank"
                      );
                    }}
                  >
                    <PostAvatar
                      className={"!min-w-8 !h-8"}
                      src={userDetails?.profilePicture}
                    />
                    <div>
                      <h1 className="text-black dark:text-white text-base">
                        {userDetails?.name}
                      </h1>
                      <span className="text-black/30 dark:text-white/70">
                        {userDetails?.username}
                      </span>
                      {
                        isUserLocationOn &&
                        <span className="text-black/30 dark:text-white/70"> | {lastLocation("last_location")}: {currentUserIpLocation}</span>
                      }
                    </div>
                  </div>

                  <div
                    className={`${user?.userId === userDetails?.id && "invisible"
                      }`}
                  >
                    {isFollowing(currentUserFollowing, userDetails) ? (
                      <SolidButton
                        className={`!py-1.5 transition ease-in-out !bg-gray-400 cursor-pointer ${loader && "cursor-not-allowed opacity-60"
                          }`}
                        onClick={() => {
                          if (!isOpen) {
                            setIsOpen(true)
                          }
                        }}
                        loading={loader}
                        disabled={loader}
                      >
                        {explorePostDetailsT("following")}
                      </SolidButton>
                    ) : (
                      <SolidButton
                        className={`!py-1.5 transition ease-in-out cursor-pointer ${loader && "cursor-not-allowed opacity-60"
                          }`}
                        onClick={() => followThisUser(userDetails?.id)}
                        loading={loader}
                        disabled={loader}
                      >
                        {explorePostDetailsT("follow")}
                      </SolidButton>
                    )}
                  </div>
                </div>
                {userDetails?.bio && (
                  <div className="w-full">
                    <pre className="max-w-[95%] truncate text-nowrap overflow-y-hidden text-black/60 text-xs font-sans dark:text-white font-normal">
                      {userDetails?.bio}
                    </pre>
                  </div>
                )}
                <div
                  className="w-full flex items-center gap-4 text-xs text-nowrap text-black/30 tracking-tight"
                // onClick={(e) => {
                //   e.stopPropagation();
                //   window.open( userDetails?.id, "_blank");
                // }}
                >
                  <p className="text-inherit text-black dark:text-white">
                    {userDetails?._count?.followers}{" "}
                    {explorePostDetailsT("followers")}
                  </p>
                  <p className="text-inherit text-black dark:text-white">
                    {userDetails?._count?.following}{" "}
                    {explorePostDetailsT("followings")}
                  </p>
                  <p className="text-inherit text-black dark:text-white">
                    {userDetails?._count?.likes + userDetails?._count?.collect}{" "}
                    {explorePostDetailsT("likes_and_fav")}
                  </p>
                </div>
                <div className="w-full flex items-center justify-between gap-2 my-auto">
                  {userPosts.length === 0 && (
                    <span className="w-full text-center text-xs text-black/30">
                      {explorePostDetailsT("nothing_to_show")}
                    </span>
                  )}
                  {userPosts.map((post, i) => (
                    <div
                      key={i}
                      className="h-[100px] w-[100px] rounded border dark:border-brown overflow-hidden cursor-pointer relative group"
                      onClick={(e) => {
                        e.stopPropagation();
                        const permanentPath = buildContentPermanentPath(post);
                        if (permanentPath) window.open(permanentPath, "_blank");
                      }}
                    >
                      <div className="absolute top-0 left-0 w-full h-full bg-black/20 opacity-0 group-hover:opacity-100 z-10 transition" />
                      {post?.media[0]?.mediaType?.includes("image") ? (
                        <img
                          src={
                            post?.media[0]?.filePath?.startsWith("http")
                              ? post?.media[0]?.filePath
                              : post?.media[0]?.filePath?.startsWith("uploads/") ||
                                post?.media[0]?.filePath?.startsWith("/")
                              ? `${CLOUDFRONT_BASE_URL}/${post?.media[0]?.filePath}`
                              : post?.media[0]?.filePath
                          }
                          alt="post"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={
                            post?.media[0]?.filePath?.startsWith("http")
                              ? post?.media[0]?.filePath
                              : post?.media[0]?.filePath?.startsWith("uploads/") ||
                                post?.media[0]?.filePath?.startsWith("/")
                              ? `${CLOUDFRONT_BASE_URL}/${post?.media[0]?.filePath}`
                              : post?.media[0]?.filePath
                          }
                          alt="post"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        }
        open={isVisible}
        onOpen={() => setIsVisible(true)}
        onClose={() => setIsVisible(false)}
        onClick={(e) => e.stopPropagation()}
        placement="bottom-start"
      >
        <div onClick={(e) => e.stopPropagation()} className="inline-block">
          {children}
        </div>
      </Tooltip>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={explorePostDetailsT("are_you_sure_unfollow")}
      >
        <ChatConfirmationModal
          onOkText={explorePostDetailsT("unfollow")}
          onCancel={() => setIsOpen(false)}
          onOk={handleUnfollowUser}
        />
      </Modal>
    </>
  );
};

export default TooltipUserNameheading;
